import React, { useEffect, useMemo, useRef } from 'react';

import { Button, View, XStack } from 'tamagui';

// Web-only imports - these will only be bundled for web platform
import config from '@/config/config';
import {
  MapWebPopup,
  MapWebPopupContent,
} from '@components/MapView/Popup/popup.styled';
import { ProjectMapProps } from '@components/MapView/ProjectMap';
import { Ionicons } from '@expo/vector-icons';
import mapboxgl, { GeoJSONFeature, MapMouseEvent } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

import marker from '@assets/images/marker.png';
import { MAP_CLUSTER_LAYOUTS } from '@components/MapView/ProjectMap.constants';
import { GeoJSONFeatureCollection } from '@components/MapView/types/mapView.types';
import mapItemToFeatureCollection from '@components/MapView/utils/mapItemToFeatureCollection';
import { usePagination } from '@components/Pagination/hooks/usePagination';
import { Pagination } from '@components/Pagination/Pagination';

export default function ProjectMapWeb<Item>({
  items,
  popup,
  onSelectItem,
}: ProjectMapProps<Item>): React.ReactElement {
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const sourceId = 'map-items-source';
  const clusterLayerId = 'clusters';
  const countLayerId = 'cluster-count';
  const unclusteredLayerId = 'unclustered-points';

  const {
    currentPageItems,
    totalPages,
    currentPage,
    goToFirstPage,
    setItems,
    setPage,
  } = usePagination<Item>({
    pageSize: 1,
    items: [],
  });

  const selectedItem =
    currentPageItems.length > 0 ? currentPageItems[0] : undefined;

  // Convert items to GeoJSON features
  const geojsonData = useMemo((): GeoJSONFeatureCollection<Item> => {
    return mapItemToFeatureCollection(items);
  }, [items]);

  const onClick = (e: MapMouseEvent): void => {
    const features = mapRef.current?.queryRenderedFeatures(e.point, {
      layers: [clusterLayerId, unclusteredLayerId],
    });

    if (!features || features.length === 0) return;

    const feature: GeoJSONFeature = features[0];

    if (feature.properties?.point_count) {
      // It's a cluster - zoom to it
      const clusterId = feature.properties.cluster_id;
      const source = mapRef.current?.getSource(
        sourceId,
      ) as mapboxgl.GeoJSONSource;

      if (!clusterId) return;

      source.getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err || !mapRef.current) return;

        if (
          feature.geometry.type === 'Point' &&
          feature.geometry.coordinates.length === 2
        )
          mapRef.current.easeTo({
            center: feature.geometry.coordinates as [number, number],
            zoom: zoom || +unclusteredLayerId,
          });
      });
    } else {
      // It's a single point - select it
      if (feature.properties?.originalItem) {
        const parsedItems = features
          .filter((f) => f.layer?.id === 'unclustered-points')
          .map((f) => {
            const featureItem = f.properties?.originalItem;
            try {
              return JSON.parse(featureItem);
            } catch (e) {
              console.error(`Cannot parse map feature. Error: ${e}`);
            }
            return undefined;
          });

        setItems(parsedItems);
        goToFirstPage();
      }
    }
  };

  useEffect(() => {
    if (!mapContainerRef.current) return;

    mapboxgl.accessToken = config.mapbox.accessToken;
    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: config.mapbox.style,
    });

    mapRef.current.on('load', () => {
      if (!mapRef.current) return;

      // Add GeoJSON source with clustering enabled
      mapRef.current.addSource(sourceId, {
        type: 'geojson',
        data: geojsonData,
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 10,
      });

      // Add cluster layer
      mapRef.current.addLayer({
        id: clusterLayerId,
        type: 'circle',
        source: sourceId,
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': MAP_CLUSTER_LAYOUTS.CIRCLE_COLOR,
          'circle-radius': MAP_CLUSTER_LAYOUTS.CIRCLE_RADIUS,
        },
      });

      // Add cluster count layer
      mapRef.current.addLayer({
        id: countLayerId,
        type: 'symbol',
        source: sourceId,
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
        },
        paint: {
          'text-color': '#ffffff',
        },
      });

      // Add unclustered points layer
      mapRef.current.addLayer({
        id: unclusteredLayerId,
        type: 'symbol',
        source: sourceId,
        filter: ['!', ['has', 'point_count']],
        layout: {
          'icon-image': 'custom_marker',
          'icon-size': 0.5,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-anchor': 'bottom',
          'icon-offset': [0, 0],
        },
      });

      // Handle click on clusters and points
      mapRef.current.on('click', onClick);

      // Handle mouse enter on clusters
      mapRef.current.on('mouseenter', clusterLayerId, () => {
        if (mapRef.current) {
          mapRef.current.getCanvas().style.cursor = 'pointer';
        }
      });

      mapRef.current.on('mouseleave', clusterLayerId, () => {
        if (mapRef.current) {
          mapRef.current.getCanvas().style.cursor = '';
        }
      });

      // Handle mouse enter on unclustered points
      mapRef.current.on('mouseenter', unclusteredLayerId, () => {
        if (mapRef.current) {
          mapRef.current.getCanvas().style.cursor = 'pointer';
        }
      });

      mapRef.current.on('mouseleave', unclusteredLayerId, () => {
        if (mapRef.current) {
          mapRef.current.getCanvas().style.cursor = '';
        }
      });

      // Load the marker image
      mapRef.current.loadImage(marker.uri, (error, image) => {
        if (error) throw error;
        if (mapRef.current)
          mapRef.current.addImage('custom_marker', image as ImageData);
      });
    });

    return (): void => {
      mapRef.current?.remove();
    };
  }, []); // Empty dependency array to run only once

  // Update source data when items change
  useEffect(() => {
    if (!mapRef.current) return;

    const source = mapRef.current.getSource(sourceId) as mapboxgl.GeoJSONSource;
    if (source) {
      source.setData(geojsonData);
    }
  }, [geojsonData, sourceId]);

  useEffect(() => {
    if (onSelectItem) {
      onSelectItem(selectedItem);
    }
  }, [selectedItem, onSelectItem]);

  return (
    <XStack flex={1} paddingVertical="$2">
      <View width="100%" height="100%" ref={mapContainerRef}>
        {/** Rendering map popup */}
        {popup && (
          <MapWebPopup>
            <XStack marginBottom="0.5rem" paddingHorizontal="$1">
              <Button
                width="1rem"
                onPress={() => setItems([])}
                icon={
                  <Ionicons name="chevron-back" size={20} color="inherit" />
                }
              />
            </XStack>
            <MapWebPopupContent>{popup}</MapWebPopupContent>
            <XStack
              width="100%"
              display="flex"
              justifyContent="center"
              backgroundColor="white"
            >
              <XStack>
                <Pagination
                  page={currentPage}
                  count={totalPages}
                  onChange={(page) => setPage(page)}
                />
              </XStack>
            </XStack>
          </MapWebPopup>
        )}
      </View>
    </XStack>
  );
}
